import React, { useEffect, useRef, useState, useCallback } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { db } from "../firebase";

const containerStyle = { 
  width: "100%", 
  height: "600px",
  borderRadius: "10px",
  border: "2px solid #dee2e6"
};

const toLatLngLiteral = (coord: any[]) => ({ lat: coord[1], lng: coord[0] });

interface Company {
  companyId: string;
  companyName: string;
}

interface DriverLocation {
  driverId: string;
  position: google.maps.LatLngLiteral;
  tripId: string;
  phoneNumber: string;
  schoolName: string;
  pickupCount: number;
  dropCount: number;
  tripSubType: string;
  driverName?: string;
  vehicleName?: string;
  lastUpdated?: Date;
  isActive?: boolean;
}

interface Trip {
  id: string;
  activityLogId: string;
  companyId: string;
  vehicle: any;
  status: string;
  tripStatus: string;
  tripSubType: string;
  startLocation?: { coordinates: number[] };
  endLocation?: { coordinates: number[] };
  customer?: any[];
  driver?: any;
  pickedCount: number;
  company?: any;
  tenantId?: string;
}

export default function AdminLiveTrackingMap() {
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places", "geometry"]
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRefs = useRef<{ [key: string]: any }>({});
  const animationFrames = useRef<{ [key: string]: number }>({});
  const locationListenersRef = useRef<{ [key: string]: () => void }>({});
  
  const [adminCompanies, setAdminCompanies] = useState<Company[]>([]);
  const [activeTrips, setActiveTrips] = useState<Trip[]>([]);
  const [driverLocations, setDriverLocations] = useState<DriverLocation[]>([]);

  // Helper to deduplicate driver locations by driverId+tripId
  const upsertDriverLocation = useCallback((updater: (prev: DriverLocation[]) => DriverLocation[]) => {
    setDriverLocations(prev => {
      const next = updater(prev);
      const seen = new Set<string>();
      return next.filter(loc => {
        const key = `${loc.driverId}-${loc.tripId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    });
  }, []);
  const [selectedDriver, setSelectedDriver] = useState<DriverLocation | null>(null);
  const [center, setCenter] = useState({ lat: 24.8607, lng: 67.0011 });
  const [zoom, setZoom] = useState(13);
  const [getCurrentUser, setCurrentUser] = useState<any>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>(new Date().toLocaleTimeString());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [shouldAutoFit, setShouldAutoFit] = useState<boolean>(true);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [activeDriversCount, setActiveDriversCount] = useState<number>(0);

  const activeTripsRef = useRef<Trip[]>([]);
  const selectedDriverRef = useRef<DriverLocation | null>(null);
  const shouldAutoFitRef = useRef<boolean>(true);

  // Keep refs in sync with state
  useEffect(() => { activeTripsRef.current = activeTrips; }, [activeTrips]);
  useEffect(() => { selectedDriverRef.current = selectedDriver; }, [selectedDriver]);
  useEffect(() => { shouldAutoFitRef.current = shouldAutoFit; }, [shouldAutoFit]);

  // Custom icons - Predefined
  const getIcons = () => {
    if (!isLoaded) return null;

    return {
      car: {
        url: 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 20 L8 25 L12 25 L14 20 Z M25 20 L23 25 L27 25 L29 20 Z" fill="#333"/>
            <rect x="8" y="15" width="24" height="10" rx="2" fill="#007bff"/>
            <rect x="10" y="12" width="20" height="8" rx="1" fill="#0056b3"/>
            <circle cx="15" cy="28" r="4" fill="#333"/>
            <circle cx="25" cy="28" r="4" fill="#333"/>
            <circle cx="15" cy="28" r="2" fill="#fff"/>
            <circle cx="25" cy="28" r="2" fill="#fff"/>
          </svg>
        `))),
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 40)
      },
      selectedCar: {
        url: 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(`
          <svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 25 L10 30 L14 30 L16 25 Z M30 25 L28 30 L32 30 L34 25 Z" fill="#333"/>
            <rect x="10" y="15" width="30" height="10" rx="2" fill="#ff9800"/>
            <rect x="12" y="12" width="26" height="8" rx="1" fill="#e65100"/>
            <circle cx="18" cy="33" r="5" fill="#333"/>
            <circle cx="32" cy="33" r="5" fill="#333"/>
            <circle cx="18" cy="33" r="2.5" fill="#fff"/>
            <circle cx="32" cy="33" r="2.5" fill="#fff"/>
            <circle cx="25" cy="15" r="8" fill="#ff9800" stroke="#fff" stroke-width="2"/>
            <text x="25" y="19" text-anchor="middle" fill="#fff" font-size="8" font-weight="bold">!</text>
          </svg>
        `))),
        scaledSize: new window.google.maps.Size(50, 50),
        anchor: new window.google.maps.Point(25, 50)
      },
      inactiveCar: {
        url: 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 20 L8 25 L12 25 L14 20 Z M25 20 L23 25 L27 25 L29 20 Z" fill="#999"/>
            <rect x="8" y="15" width="24" height="10" rx="2" fill="#6c757d"/>
            <rect x="10" y="12" width="20" height="8" rx="1" fill="#495057"/>
            <circle cx="15" cy="28" r="4" fill="#999"/>
            <circle cx="25" cy="28" r="4" fill="#999"/>
            <circle cx="15" cy="28" r="2" fill="#dee2e6"/>
            <circle cx="25" cy="28" r="2" fill="#dee2e6"/>
            <path d="M8,8 L32,32" stroke="#dc3545" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `))),
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 40)
      }
    };
  };

  // Fetch user data from localStorage
  useEffect(() => {
    const fetchUser = () => {
      try {
        const userData = localStorage.getItem("user");
        if (userData) {
          const userJson = JSON.parse(userData);
          setCurrentUser(userJson);
          const companies: Company[] = userJson.company || [];
          const uniqueCompanies = Array.from(new Map(companies.map((c) => [c.companyId, c])).values());
          setAdminCompanies(uniqueCompanies);
          console.log("User loaded:", userJson.email);
        } else {
          console.warn("No user found in localStorage");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUser();
  }, []);

  // Fetch active trips - FIXED VERSION
  useEffect(() => {
    if (!getCurrentUser) {
      console.log("Waiting for user data...");
      return;
    }

    const isSuperAdmin = getCurrentUser?.role === "superAdmin";

    if (!isSuperAdmin && (adminCompanies.length === 0 || !getCurrentUser?.tenantId)) {
      console.log("Waiting for tenant/company data...");
      return;
    }

    console.log("Fetching trips for:", {
      role: getCurrentUser?.role,
      isSuperAdmin,
      companyCount: adminCompanies.length,
      tenantId: getCurrentUser.tenantId,
      companies: adminCompanies
    });

    const companyIds = adminCompanies.map(c => c.companyId);
    
    // Super admin can view all active running trips.
    // Other users remain scoped to their own tenant/company.
    const q = isSuperAdmin
      ? query(
          collection(db, "trip"),
          where("status", "==", "rideStarted"),
          where("tripStatus", "==", "onGoing")
        )
      : query(
          collection(db, "trip"),
          where("company.companyId", "in", companyIds),
          where("tenantId", "==", getCurrentUser.tenantId),
          where("status", "==", "rideStarted"),
          where("tripStatus", "==", "onGoing")
        );

    const unsub = onSnapshot(q, 
      (snapshot) => {
        console.log(`🔥 Firestore snapshot received: ${snapshot.size} trips`);
        
        const tripsMap = new Map<string, Trip>();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const activityLogId = data.activityLogId || doc.id;
          if (!tripsMap.has(activityLogId)) {
            tripsMap.set(activityLogId, {
              id: doc.id,
              activityLogId,
              ...data
            } as Trip);
          }
        });
        const trips = Array.from(tripsMap.values());
        
        console.log("Active trips found:", trips.map(t => ({
          id: t.id,
          driverName: t.driver?.driverName,
          status: t.status,
          tripStatus: t.tripStatus,
          pickedCount: t.pickedCount
        })));
        
        // Handle trip removal when status changes to rideEnded
        const currentTripIds = trips.map(t => t.activityLogId);
        const previousTripIds = activeTripsRef.current.map(t => t.activityLogId);
        
        // Find trips that are no longer active (rideEnded)
        const removedTrips = previousTripIds.filter(id => !currentTripIds.includes(id));
        
        if (removedTrips.length > 0) {
          console.log("Trips removed (rideEnded):", removedTrips);
          
          // Remove driver locations for ended trips
          upsertDriverLocation(prev => {
            const updated = prev.filter(driver => 
              !removedTrips.includes(driver.tripId)
            );
            console.log(`Removed ${prev.length - updated.length} driver locations`);
            return updated;
          });
          
          // Clean up listeners for ended trips
          removedTrips.forEach(tripId => {
            Object.keys(locationListenersRef.current).forEach(key => {
              if (key.includes(tripId)) {
                locationListenersRef.current[key]();
                delete locationListenersRef.current[key];
              }
            });
          });
          
          // If selected trip is ended, deselect it
          if (selectedTripId && removedTrips.includes(selectedTripId)) {
            setSelectedDriver(null);
            setSelectedTripId(null);
          }
        }
        
        setActiveTrips(trips);
        
        // Set up listeners for new trips
        trips.forEach(trip => {
          if (trip.driver?.driverId && trip.activityLogId) {
            setupDriverLocationListener(trip);
          }
        });
        
        // Auto-fit map if enabled
        if (shouldAutoFit && trips.length > 0 && mapRef.current) {
          setTimeout(() => {
            fitMapToAllDrivers();
          }, 1000);
        }
      },
      (error) => {
        console.error("Firestore error:", error);
      }
    );

    return () => unsub();
  }, [adminCompanies, getCurrentUser]);

  // Setup driver location listener - uses refs to avoid re-creating listeners
  const setupDriverLocationListener = useCallback((trip: Trip) => {
    if (!trip.driver?.driverId || !trip.activityLogId) return;

    const driverKey = `${trip.driver.driverId}-${trip.activityLogId}`;
    
    // Clean up existing listener
    if (locationListenersRef.current[driverKey]) {
      locationListenersRef.current[driverKey]();
      delete locationListenersRef.current[driverKey];
    }

    const driverLocationRef = doc(db, "trip", trip.activityLogId, "riderLocation", trip.driver.driverId);
    
    console.log(`👂 Setting up listener for driver: ${trip.driver.driverName} (${driverKey})`);

    const unsub = onSnapshot(driverLocationRef, 
      (snap) => {
        const updateTime = new Date();
        
        if (!snap.exists()) {
          console.log(`📭 Driver ${trip.driver.driverName} location not found`);
          
          upsertDriverLocation(prev => {
            const existingIndex = prev.findIndex(
              loc => loc.driverId === trip.driver.driverId && loc.tripId === trip.activityLogId
            );

            if (existingIndex >= 0) {
              const updated = [...prev];
              updated[existingIndex] = {
                ...updated[existingIndex],
                isActive: false,
                lastUpdated: updateTime
              };
              return updated;
            }
            return prev;
          });
          
          return;
        }

        const data = snap.data();
        const coords = data?.location?.coordinates;
        
        if (coords?.length >= 2) {
          const newPosition = toLatLngLiteral(coords);
          
          upsertDriverLocation(prev => {
            const existingIndex = prev.findIndex(
              loc => loc.driverId === trip.driver.driverId && loc.tripId === trip.activityLogId
            );

            const newLocation: DriverLocation = {
              driverId: trip.driver.driverId,
              position: newPosition,
              tripId: trip.activityLogId,
              driverName: trip.driver.driverName || "Driver",
              vehicleName: trip.vehicle?.vehicleName || "Vehicle",
              phoneNumber: trip.driver.phoneNumber || "",
              schoolName: trip.company?.companyName || "",
              tripSubType: trip.tripSubType || "",
              pickupCount: trip.pickedCount || 0,
              dropCount: trip.customer?.filter((c: any) => c.riderStatus === "dropped").length || 0,
              lastUpdated: updateTime,
              isActive: true
            };

            const selDriver = selectedDriverRef.current;
            if (selDriver && 
                selDriver.driverId === trip.driver.driverId && 
                selDriver.tripId === trip.activityLogId) {
              setSelectedDriver(newLocation);
              if (!shouldAutoFitRef.current && mapRef.current) {
                mapRef.current.panTo(newPosition);
              }
            }

            setLastUpdateTime(updateTime.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            }));

            if (existingIndex >= 0) {
              const updated = [...prev];
              const prevLocation = updated[existingIndex];
              if (prevLocation.position.lat !== newPosition.lat || 
                  prevLocation.position.lng !== newPosition.lng) {
                animateMarker(driverKey, prevLocation.position, newPosition);
              }
              updated[existingIndex] = newLocation;
              return updated;
            } else {
              return [...prev, newLocation];
            }
          });
          
          console.log(`📍 Driver ${trip.driver.driverName} location updated`);
        }
      },
      (error) => {
        console.error(`Error in location listener for driver ${trip.driver.driverId}:`, error);
      }
    );

    locationListenersRef.current[driverKey] = unsub;
  }, []);

  // Clean up all listeners
  useEffect(() => {
    return () => {
      Object.values(locationListenersRef.current).forEach(unsub => unsub());
      locationListenersRef.current = {};
      
      Object.values(animationFrames.current).forEach(frameId => {
        cancelAnimationFrame(frameId);
      });
      animationFrames.current = {};
    };
  }, []);

  // Real-time trip updates listener - use refs to avoid cascading re-runs

  // Only re-run when trip IDs change (not when data updates)
  const tripIdsKey = activeTrips.map(t => t.activityLogId).sort().join(",");
  const prevTripIdsKeyRef = useRef<string>("");

  useEffect(() => {
    if (activeTrips.length === 0) return;
    if (tripIdsKey === prevTripIdsKeyRef.current) return;
    prevTripIdsKeyRef.current = tripIdsKey;

    console.log("Setting up real-time trip updates listener for trips:", activeTrips.map(t => t.activityLogId));

    const unsubscribeFunctions: (() => void)[] = [];

    activeTrips.forEach((trip) => {
      const tripDocRef = doc(db, "trip", trip.activityLogId);
      
      const unsub = onSnapshot(tripDocRef, 
        (snap) => {
          if (!snap.exists()) {
            console.log(`Trip ${trip.activityLogId} no longer exists`);
            return;
          }

          const data = snap.data();
          const newStatus = data.status;
          const newTripStatus = data.tripStatus;
          const isStillActive = newStatus === "rideStarted" && newTripStatus === "onGoing";
          
          console.log(`Trip ${trip.activityLogId} updated:`, {
            status: newStatus,
            tripStatus: newTripStatus,
            pickedCount: data.pickedCount
          });

          // Update active trips using functional updater (reads latest state)
          setActiveTrips(prev => {
            const next = prev.map(t => {
              if (t.activityLogId === trip.activityLogId) {
                return {
                  ...t,
                  pickedCount: data.pickedCount || 0,
                  customer: data.customer || [],
                  status: newStatus || t.status,
                  tripStatus: newTripStatus || t.tripStatus
                };
              }
              return t;
            }).filter(t => t.status === "rideStarted" && t.tripStatus === "onGoing");
            return next;
          });

          // Update driver locations using functional updater
          upsertDriverLocation(prev => {
            if (!isStillActive) {
              return prev.filter(driver => driver.tripId !== trip.activityLogId);
            }
            
            return prev.map(driver => {
              if (driver.tripId === trip.activityLogId) {
                const dropCount = data.customer
                  ? data.customer.filter((c: any) => c.riderStatus === "dropped").length
                  : 0;
                
                return {
                  ...driver,
                  pickupCount: data.pickedCount || 0,
                  dropCount: dropCount,
                  lastUpdated: new Date(),
                  isActive: true
                };
              }
              return driver;
            });
          });

          // Update selected driver using ref (no dependency needed)
          const selDriver = selectedDriverRef.current;
          if (selDriver && selDriver.tripId === trip.activityLogId) {
            if (!isStillActive) {
              setSelectedDriver(null);
              setSelectedTripId(null);
            } else {
              const dropCount = data.customer
                ? data.customer.filter((c: any) => c.riderStatus === "dropped").length
                : 0;
              
              setSelectedDriver(prev => prev ? {
                ...prev,
                pickupCount: data.pickedCount || 0,
                dropCount: dropCount,
                lastUpdated: new Date()
              } : null);
            }
          }
        },
        (error) => {
          console.error(`Error listening to trip ${trip.activityLogId}:`, error);
        }
      );

      unsubscribeFunctions.push(unsub);
    });

    return () => {
      unsubscribeFunctions.forEach(unsub => unsub());
    };
  }, [tripIdsKey]);

  // Update active drivers count
  useEffect(() => {
    const activeDrivers = driverLocations.filter(driver => driver.isActive);
    setActiveDriversCount(activeDrivers.length);
  }, [driverLocations]);

  // Handle marker load
  const handleMarkerLoad = useCallback((marker: any, driverId: string, tripId: string) => {
    const key = `${driverId}-${tripId}`;
    markerRefs.current[key] = marker;
  }, []);

  // Smooth marker animation
  const animateMarker = useCallback((markerKey: string, startPos: google.maps.LatLngLiteral, endPos: google.maps.LatLngLiteral) => {
    const marker = markerRefs.current[markerKey];
    if (!marker || !window.google?.maps) return;

    if (animationFrames.current[markerKey]) {
      cancelAnimationFrame(animationFrames.current[markerKey]);
    }

    const startTime = Date.now();
    const duration = 2000;

    const animate = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      const easeProgress = progress < 0.5 
        ? 2 * progress * progress 
        : -1 + (4 - 2 * progress) * progress;
      
      const currentLat = startPos.lat + (endPos.lat - startPos.lat) * easeProgress;
      const currentLng = startPos.lng + (endPos.lng - startPos.lng) * easeProgress;
      
      marker.setPosition({ lat: currentLat, lng: currentLng });
      
      if (progress < 1) {
        animationFrames.current[markerKey] = requestAnimationFrame(animate);
      } else {
        marker.setPosition(endPos);
        delete animationFrames.current[markerKey];
      }
    };

    animationFrames.current[markerKey] = requestAnimationFrame(animate);
  }, []);

  // Fit map to all drivers
  const fitMapToAllDrivers = useCallback(() => {
    if (!mapRef.current || driverLocations.length === 0 || !isLoaded) return;

    const activeDrivers = driverLocations.filter(driver => driver.isActive);
    if (activeDrivers.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    activeDrivers.forEach(driver => {
      bounds.extend(new window.google.maps.LatLng(driver.position.lat, driver.position.lng));
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
      
      console.log("🗺️ Map fitted to show all active drivers");
    }
  }, [driverLocations, isLoaded]);

  // Auto-fit bounds when drivers change
  useEffect(() => {
    if (!mapRef.current || driverLocations.length === 0 || !isLoaded || !shouldAutoFit) return;

    const timer = setTimeout(() => {
      fitMapToAllDrivers();
    }, 1000);

    return () => clearTimeout(timer);
  }, [driverLocations, isLoaded, shouldAutoFit, fitMapToAllDrivers]);

  // Handle map bounds change
  const handleBoundsChanged = useCallback(() => {
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        setShouldAutoFit(false);
      }
    }
  }, []);

  // Handle zoom change
  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) {
      const newZoom = mapRef.current.getZoom();
      if (newZoom !== undefined) {
        setZoom(newZoom);
        setShouldAutoFit(false);
      }
    }
  }, []);

  // Handle driver selection
  const handleSelectDriver = useCallback((driver: DriverLocation) => {
    setSelectedDriver(driver);
    setSelectedTripId(driver.tripId);
    setShouldAutoFit(false);
    
    if (mapRef.current) {
      // Smooth pan to driver position
      mapRef.current.panTo(driver.position);
      setZoom(15);
    }
  }, []);

  // Reset view
  const resetView = useCallback(() => {
    setSelectedDriver(null);
    setSelectedTripId(null);
    setCenter({ lat: 24.8607, lng: 67.0011 });
    setZoom(13);
    setShouldAutoFit(true);
    
    if (mapRef.current) {
      mapRef.current.panTo({ lat: 24.8607, lng: 67.0011 });
    }
  }, []);

  if (loadError) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: '#721c24', 
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        borderRadius: '8px'
      }}>
        <h3>❌ Map Loading Error</h3>
        <p>Google Maps failed to load. Please check your API key and internet connection.</p>
      </div>
    );
  }

  if (!isLoaded || isLoading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: '20px'
        }}>
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>
            <h4>Loading Live Tracking Map...</h4>
            <p style={{ color: '#666' }}>Please wait while we load the tracking system</p>
          </div>
        </div>
      </div>
    );
  }

  const icons = getIcons();
  const activeDriverLocations = driverLocations.filter(driver => driver.isActive);

  return (
    <div style={{ height: "100%", overflow: "auto", position: 'relative', padding: '10px' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '15px 20px',
        borderRadius: '10px',
        marginBottom: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h3 style={{ margin: 0 }}>🚗 Real-time Driver Tracking System</h3>
          <p style={{ margin: '5px 0 0 0', opacity: 0.9, fontSize: '14px' }}>
            Live tracking across all schools • Auto-fit: {shouldAutoFit ? "ON" : "OFF"} • Last Update: {lastUpdateTime}
          </p>
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.2)',
          padding: '8px 15px',
          borderRadius: '20px',
          fontSize: '14px',
          display: 'flex',
          gap: '10px'
        }}>
          <div>
            <span style={{ fontWeight: 'bold' }}>{activeDriversCount}</span> Active Drivers
          </div>
          <div style={{ width: '1px', background: 'rgba(255,255,255,0.3)' }}></div>
          <div>
            <span style={{ fontWeight: 'bold' }}>{activeTrips.length}</span> Active Trips
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '15px' }}>
        {/* Left Sidebar */}
        <div style={{ width: '300px', flexShrink: 0 }}>
          {/* Schools Panel */}
          <div style={{
            background: 'white',
            padding: '15px',
            borderRadius: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            marginBottom: '15px',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
              🏫 Schools ({adminCompanies.length})
            </h4>
            <div>
              {adminCompanies.map(company => (
                <div key={company.companyId} style={{
                  padding: '8px',
                  margin: '5px 0',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  borderLeft: '4px solid #007bff',
                  fontSize: '14px'
                }}>
                  {company.companyName}
                </div>
              ))}
            </div>
          </div>

          {/* Stats Panel */}
          <div style={{
            background: 'white',
            padding: '15px',
            borderRadius: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            marginBottom: '15px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>📊 Statistics</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div style={{ textAlign: 'center', padding: '10px', background: '#e3f2fd', borderRadius: '6px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>
                  {activeTrips.length}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Active Trips</div>
              </div>
              <div style={{ textAlign: 'center', padding: '10px', background: '#e8f5e9', borderRadius: '6px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>
                  {activeDriversCount}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Live Drivers</div>
              </div>
              <div style={{ textAlign: 'center', padding: '10px', background: '#fff3e0', borderRadius: '6px' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>
                  {adminCompanies.length}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Schools</div>
              </div>
              <div style={{ textAlign: 'center', padding: '10px', background: '#f3e5f5', borderRadius: '6px' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#7b1fa2' }}>
                  {lastUpdateTime}
                </div>
                <div style={{ fontSize: '10px', color: '#666' }}>Last Update</div>
              </div>
            </div>
          </div>

          {/* Auto-fit Toggle */}
          <div style={{
            background: 'white',
            padding: '15px',
            borderRadius: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            marginBottom: '15px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4 style={{ margin: 0, color: '#333' }}>🗺️ Map Settings</h4>
              <span style={{
                padding: '4px 10px',
                background: shouldAutoFit ? '#28a745' : '#6c757d',
                color: 'white',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold'
              }}>
                {shouldAutoFit ? 'AUTO-FIT ON' : 'AUTO-FIT OFF'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setShouldAutoFit(true);
                  setTimeout(() => fitMapToAllDrivers(), 100);
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: shouldAutoFit ? '#28a745' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Enable Auto-fit
              </button>
              <button
                onClick={() => setShouldAutoFit(false)}
                style={{
                  flex: 1,
                  padding: '8px',
                  background: !shouldAutoFit ? '#dc3545' : '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Disable Auto-fit
              </button>
            </div>
          </div>

          {/* Active Trips List */}
          <div style={{
            background: 'white',
            padding: '15px',
            borderRadius: '10px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
              🚀 Active Trips ({activeTrips.length})
            </h4>
            {activeTrips.length > 0 ? (
              <div>
                {activeTrips.map(trip => {
                  const driverLoc = driverLocations.find(
                    loc => loc.tripId === trip.activityLogId && loc.driverId === trip.driver?.driverId
                  );
                  
                  const isSelected = selectedTripId === trip.activityLogId;
                  const isActive = driverLoc?.isActive || false;
                  
                  return (
                    <div 
                      key={trip.id}
                      style={{
                        padding: '12px',
                        margin: '8px 0',
                        background: isSelected ? '#ffebee' : (isActive ? '#e8f4fd' : '#f8f9fa'),
                        border: `2px solid ${isSelected ? '#dc3545' : (isActive ? '#007bff' : '#6c757d')}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onClick={() => {
                        if (driverLoc) {
                          handleSelectDriver(driverLoc);
                        }
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(5px)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                          {trip.driver?.driverName || 'Unknown Driver'}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {isSelected && (
                            <span style={{
                              padding: '2px 6px',
                              background: '#dc3545',
                              color: 'white',
                              borderRadius: '10px',
                              fontSize: '10px',
                              fontWeight: 'bold'
                            }}>
                              SELECTED
                            </span>
                          )}
                          <span style={{
                            padding: '3px 10px',
                            background: isActive ? '#28a745' : '#6c757d',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            {isActive ? '📍 LIVE' : '⚫ ENDED'}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                        🚗 {trip.vehicle?.vehicleName || 'No Vehicle'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '3px' }}>
                        🆔 {trip.activityLogId.substring(0, 8)}...
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: isActive ? '#28a745' : '#666', 
                        marginTop: '5px',
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        {trip.tripSubType === 'homeToOffice' ? (
                          <span>👥 Picked: {trip.pickedCount || 0}</span>
                        ) : trip.tripSubType === 'officeToHome' ? (
                          <span>👥 Dropped: {trip.customer?.filter((c: any) => c.riderStatus === "dropped").length || 0}</span>
                        ) : null}
                        {driverLoc && driverLoc.lastUpdated && (
                          <span style={{ fontSize: '11px', color: isActive ? '#28a745' : '#888' }}>
                            {driverLoc.lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px', 
                color: '#666',
                fontStyle: 'italic'
              }}>
                No active trips found
              </div>
            )}
          </div>
        </div>

        {/* Map Container */}
        <div style={{ flex: 1 }}>
          <div style={{ position: 'relative' }}>
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={zoom}
              onLoad={(map: any) => {
                mapRef.current = map;
                console.log("🗺️ Google Maps loaded successfully");
                
                map.addListener('bounds_changed', handleBoundsChanged);
                map.addListener('zoom_changed', handleZoomChanged);
              }}
              onUnmount={() => {
                if (mapRef.current) {
                  window.google.maps.event.clearInstanceListeners(mapRef.current);
                }
              }}
              onClick={() => {
                setSelectedDriver(null);
                setSelectedTripId(null);
                setShouldAutoFit(false);
              }}
              options={{
                mapTypeControl: true,
                streetViewControl: true,
                fullscreenControl: true,
                zoomControl: true,
                gestureHandling: 'greedy',
                styles: [
                  {
                    featureType: "poi",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }]
                  }
                ]
              }}
            >
              {/* Only show ACTIVE drivers on map */}
              {icons && activeDriverLocations.map((driver) => {
                const isSelected = selectedTripId === driver.tripId;
                const markerKey = `${driver.driverId}-${driver.tripId}`;
                
                return (
                  <Marker
                    key={markerKey}
                    position={driver.position}
                    icon={isSelected ? icons.selectedCar : icons.car}
                    onLoad={(marker) => handleMarkerLoad(marker, driver.driverId, driver.tripId)}
                    onClick={() => handleSelectDriver(driver)}
                    title={`${driver.driverName || 'Driver'} - ${driver.vehicleName || 'No Vehicle'}`}
                  />
                );
              })}

              {/* Selected Driver Info Window */}
              {selectedDriver && selectedDriver.isActive && (
                <InfoWindow
                  position={selectedDriver.position}
                  onCloseClick={() => {
                    setSelectedDriver(null);
                    setSelectedTripId(null);
                  }}
                >
                  <div style={{ padding: '12px', maxWidth: '280px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px',
                      marginBottom: '10px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: '#dc3545',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold'
                      }}>
                        {selectedDriver.driverName?.charAt(0) || 'D'}
                      </div>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '16px', color: '#dc3545' }}>
                          {selectedDriver.driverName || 'Driver'}
                        </h4>
                        <p style={{ margin: '2px 0', fontSize: '14px', color: '#666' }}>
                          {selectedDriver.vehicleName || 'Vehicle'}
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                          📞 {selectedDriver.phoneNumber || 'No Phone Number'}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{ 
                      background: '#f8f9fa', 
                      padding: '10px', 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      borderRadius: '6px',
                      marginBottom: '10px'
                    }}>
                      <div style={{ fontSize: '12px', color: '#555' }}>
                        <strong>🏫 School:</strong> {selectedDriver.schoolName || 'N/A'}
                      </div>
                      {selectedDriver.tripSubType === 'homeToOffice' && (
                        <div style={{ fontSize: '12px', color: '#555' }}>
                          <strong>👥 Picked:</strong> {selectedDriver.pickupCount || 0}
                        </div>
                      )}
                      {selectedDriver.tripSubType === 'officeToHome' && (
                        <div style={{ fontSize: '12px', color: '#555' }}>
                          <strong>👥 Dropped:</strong> {selectedDriver.dropCount || 0}
                        </div>
                      )}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        fontSize: '12px',
                        color: '#555'
                      }}>
                        <strong>🕒 Last Updated:</strong>
                        <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                          {selectedDriver.lastUpdated?.toLocaleTimeString() || 'N/A'}
                        </span>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => {
                        window.open(`/view-Detail/${selectedDriver.tripId}`, '_blank');
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      📋 View Trip Details
                    </button>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>

            {/* Map Controls Overlay */}
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              gap: '5px'
            }}>
              <button
                onClick={fitMapToAllDrivers}
                style={{
                  padding: '8px 12px',
                  background: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}
                title="Fit map to show all active drivers"
              >
                🔍 Fit Active Markers
              </button>
              <button
                onClick={resetView}
                style={{
                  padding: '8px 12px',
                  background: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}
                title="Reset map to default view"
              >
                🏠 Reset View
              </button>
              <button
                onClick={() => {
                  setShouldAutoFit(!shouldAutoFit);
                  if (!shouldAutoFit) {
                    setTimeout(() => fitMapToAllDrivers(), 100);
                  }
                }}
                style={{
                  padding: '8px 12px',
                  background: shouldAutoFit ? '#28a745' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}
                title="Toggle auto-fit feature"
              >
                {shouldAutoFit ? '✅ Auto-fit ON' : '❌ Auto-fit OFF'}
              </button>
              {selectedDriver && selectedDriver.isActive && (
                <button
                  onClick={() => {
                    if (selectedDriver && mapRef.current) {
                      mapRef.current.panTo(selectedDriver.position);
                      setZoom(15);
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    background: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '13px',
                    fontWeight: 'bold'
                  }}
                  title="Go to selected driver"
                >
                  📍 Focus Selected
                </button>
              )}
            </div>
          </div>

          {/* Legend */}
          <div style={{
            display: 'flex',
            gap: '20px',
            marginTop: '15px',
            padding: '10px',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '20px',
                height: '20px',
                background: '#007bff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px'
              }}>
                🚗
              </div>
              <span style={{ fontSize: '14px' }}>Active Driver</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '25px',
                height: '25px',
                background: '#dc3545',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px'
              }}>
                🚗
              </div>
              <span style={{ fontSize: '14px' }}>Selected Driver</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                background: '#28a745',
                borderRadius: '50%',
                animation: 'pulse 1.5s infinite'
              }}></div>
              <span style={{ fontSize: '14px' }}>Live Update</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '8px',
                height: '8px',
                background: shouldAutoFit ? '#28a745' : '#dc3545',
                borderRadius: '50%'
              }}></div>
              <span style={{ fontSize: '14px' }}>
                Auto-fit: {shouldAutoFit ? 'ON' : 'OFF'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
            100% { opacity: 1; transform: scale(1); }
          }
          
          ::-webkit-scrollbar {
            width: 6px;
          }
          ::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 10px;
          }
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, #6366f1, #4f46e5);
            border-radius: 10px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(180deg, #818cf8, #6366f1);
          }
        `}
      </style>
    </div>
  );
}